import { api } from '../api.js';
import { router } from '../router.js';
import { showNotification } from '../components/notification.js';
import { showContextMenu } from '../components/context-menu.js';
import { openModal } from '../components/modal.js';
import { colorForProject } from '../utils/colors.js';
import { feverEmoji, formatPercent } from '../utils/format.js';

const COLUMNS = [
  { id: 'backlog',   title: 'Backlog'   },
  { id: 'queued',    title: 'Queued'    },
  { id: 'active',    title: 'Active'    },
  { id: 'paused',    title: 'Paused'    },
  { id: 'done',      title: 'Done'      },
  { id: 'cancelled', title: 'Cancelled' },
];

const ALLOWED = {
  backlog:   new Set(['queued', 'cancelled']),
  queued:    new Set(['backlog', 'active', 'cancelled']),
  active:    new Set(['paused', 'done', 'cancelled']),
  paused:    new Set(['active', 'cancelled']),
  done:      new Set(),
  cancelled: new Set(),
};

let _board = null;          // reference to current board state
let _container = null;      // root container for re-rendering

export async function initPipeline(container) {
  _container = container;
  container.innerHTML = '<div class="loading-spinner">Загрузка pipeline...</div>';
  try {
    _board = await api.pipeline();
    renderBoard();
  } catch (e) {
    if (e.message === '401') return;
    container.innerHTML = `<div class="view-stub">
      <h2>Pipeline</h2>
      <p style="color: var(--text-danger);">Ошибка: ${escHtml(e.message)}</p>
    </div>`;
  }
}

function renderBoard() {
  _container.innerHTML = `
    <div class="pipeline-root">
      <header class="pipeline-header">
        <h2>📋 Pipeline</h2>
        <p class="pipeline-hint">Перетаскивай карточки между колонками. ПКМ — отменить проект.</p>
      </header>
      <div class="pipeline-grid" id="pipeline-grid">
        ${COLUMNS.map(col => renderColumn(col)).join('')}
      </div>
    </div>
  `;
  attachDragHandlers();
}

function renderColumn(col) {
  const data = _board[col.id] ?? { projects: [], count: 0 };
  return `
    <section class="pipeline-col" data-status="${col.id}">
      <header class="pipeline-col-header">
        <span class="pipeline-col-title">${escHtml(col.title)}</span>
        <span class="pipeline-col-count">${data.count}</span>
      </header>
      <div class="pipeline-col-body" data-drop-target="${col.id}">
        ${data.projects.map(renderCard).join('') || '<div class="pipeline-col-empty">пусто</div>'}
      </div>
    </section>
  `;
}

function renderCard(p) {
  const fever = feverEmoji(p.fever_zone);
  const progress = formatPercent(p.progress_pct);
  const buffer = formatPercent(p.buffer_consumed_pct);
  const tasks = `${p.tasks_count ?? 0} задач${(p.done_count ?? 0) ? ` · ${p.done_count} done` : ''}`;
  return `
    <article
      class="pipeline-card"
      draggable="true"
      data-project-id="${p.id}"
      data-status="${p.status}"
      style="border-left-color: ${colorForProject(p.id)};"
    >
      <div class="pipeline-card-title">
        ${fever ? `<span class="pipeline-card-fever">${fever}</span>` : ''}
        ${escHtml(p.name)}
      </div>
      <div class="pipeline-card-meta">
        <span>прогресс ${progress}</span>
        <span>буфер ${buffer}</span>
      </div>
      <div class="pipeline-card-tasks">${escHtml(tasks)}</div>
    </article>
  `;
}

function attachDragHandlers() {
  const grid = _container.querySelector('#pipeline-grid');
  let dragSourceStatus = null;
  let dragProjectId = null;

  grid.addEventListener('dragstart', (e) => {
    const card = e.target.closest('.pipeline-card');
    if (!card) return;
    dragProjectId   = Number(card.dataset.projectId);
    dragSourceStatus = card.dataset.status;
    card.classList.add('dragging');
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(dragProjectId));

    // Mark drop targets
    for (const target of grid.querySelectorAll('.pipeline-col-body')) {
      const targetStatus = target.dataset.dropTarget;
      if (targetStatus === dragSourceStatus) continue;
      const allowed = ALLOWED[dragSourceStatus]?.has(targetStatus);
      target.classList.add(allowed ? 'drop-ok' : 'drop-forbidden');
    }
  });

  grid.addEventListener('dragend', (e) => {
    const card = e.target.closest('.pipeline-card');
    if (card) card.classList.remove('dragging');
    for (const target of grid.querySelectorAll('.pipeline-col-body')) {
      target.classList.remove('drop-ok', 'drop-forbidden', 'drop-hover');
    }
    dragSourceStatus = null;
    dragProjectId = null;
  });

  grid.addEventListener('dragover', (e) => {
    const target = e.target.closest('.pipeline-col-body');
    if (!target) return;
    const targetStatus = target.dataset.dropTarget;
    if (targetStatus === dragSourceStatus) return;
    if (!ALLOWED[dragSourceStatus]?.has(targetStatus)) return;
    e.preventDefault();
    target.classList.add('drop-hover');
  });

  grid.addEventListener('dragleave', (e) => {
    const target = e.target.closest('.pipeline-col-body');
    if (target) target.classList.remove('drop-hover');
  });

  grid.addEventListener('drop', async (e) => {
    const target = e.target.closest('.pipeline-col-body');
    if (!target || dragProjectId == null) return;
    e.preventDefault();
    const targetStatus = target.dataset.dropTarget;
    target.classList.remove('drop-hover');
    if (targetStatus === dragSourceStatus) return;
    if (!ALLOWED[dragSourceStatus]?.has(targetStatus)) {
      showNotification(
        `Переход ${dragSourceStatus} → ${targetStatus} запрещён`, 'error',
      );
      return;
    }
    await handleTransition(dragProjectId, dragSourceStatus, targetStatus);
  });

  // Card click → open Gantt for project (only if not a drag).
  grid.addEventListener('click', (e) => {
    const card = e.target.closest('.pipeline-card');
    if (!card) return;
    if (card.classList.contains('dragging')) return;
    const id = Number(card.dataset.projectId);
    router.push('/gantt/' + id);
  });

  // Right-click → context menu (cancel from any non-terminal status).
  grid.addEventListener('contextmenu', (e) => {
    const card = e.target.closest('.pipeline-card');
    if (!card) return;
    e.preventDefault();
    const id = Number(card.dataset.projectId);
    const status = card.dataset.status;
    const items = [];
    if (ALLOWED[status]?.has('cancelled')) {
      items.push({
        label: '✕ Отменить проект',
        danger: true,
        action: () => handleTransition(id, status, 'cancelled'),
      });
    } else {
      items.push({ label: 'Нет действий', disabled: true, action: () => {} });
    }
    showContextMenu(e.clientX, e.clientY, items);
  });
}

async function handleTransition(projectId, fromStatus, toStatus) {
  const proj = findProject(projectId);
  if (!proj) return;

  let payload = { project_id: projectId, new_status: toStatus };

  // Per-transition modals
  if (toStatus === 'active' && fromStatus === 'queued') {
    const ok = await openModal({
      title: 'Активировать проект',
      message: `Готов активировать «${proj.name}»? Длительности задач будут зафиксированы в baseline.`,
      confirmLabel: 'Активировать',
    });
    if (!ok) return;
  } else if (toStatus === 'paused' && fromStatus === 'active') {
    const v = await openModal({
      title: 'Поставить проект на паузу',
      message: `«${proj.name}» — укажи причину паузы:`,
      fields: [{ name: 'reason', label: 'Причина', placeholder: 'например: жду ответ от подрядчика', required: true }],
      confirmLabel: 'Поставить на паузу',
    });
    if (!v) return;
    if (!v.reason || !v.reason.trim()) {
      showNotification('Причина обязательна', 'error');
      return;
    }
    payload.reason = v.reason.trim();
  } else if (toStatus === 'done') {
    const criteria = (proj.done_criteria ?? '').trim();
    const v = await openModal({
      title: 'Завершить проект',
      message: criteria
        ? `Критерии done:\n\n${criteria}`
        : `Критерии done не заданы. Подтверди явно, что проект завершён.`,
      fields: [{ name: 'met', label: 'Все критерии выполнены (yes/no)', placeholder: 'yes', value: 'yes' }],
      confirmLabel: 'Завершить',
    });
    if (!v) return;
    const met = String(v.met ?? '').toLowerCase().startsWith('y');
    if (!met) {
      showNotification('Подтверди критерии done', 'error');
      return;
    }
    payload.done_criteria_met = true;
  } else if (toStatus === 'cancelled') {
    const v = await openModal({
      title: 'Отменить проект',
      message: `«${proj.name}» — укажи причину отмены:`,
      fields: [{ name: 'reason', label: 'Причина', placeholder: 'например: цели изменились', required: true }],
      confirmLabel: 'Отменить проект',
      danger: true,
    });
    if (!v) return;
    if (!v.reason || !v.reason.trim()) {
      showNotification('Причина обязательна', 'error');
      return;
    }
    payload.reason = v.reason.trim();
  }

  // Optimistic move
  moveCardOptimistic(projectId, fromStatus, toStatus);

  try {
    await api.pipelineMove(payload);
    showNotification(`«${proj.name}»: ${fromStatus} → ${toStatus}`, 'info');
    // Refresh from server to get updated counts/buffer/etc.
    _board = await api.pipeline();
    renderBoard();
  } catch (e) {
    moveCardOptimistic(projectId, toStatus, fromStatus);  // rollback
    const msg = e.detail?.detail ?? e.detail ?? e.message;
    showNotification('Не удалось: ' + (typeof msg === 'string' ? msg : JSON.stringify(msg)), 'error');
  }
}

function findProject(id) {
  for (const col of Object.values(_board)) {
    const p = col.projects.find(x => x.id === id);
    if (p) return p;
  }
  return null;
}

function moveCardOptimistic(projectId, fromStatus, toStatus) {
  const proj = findProject(projectId);
  if (!proj) return;
  const fromCol = _board[fromStatus];
  const toCol = _board[toStatus];
  if (!fromCol || !toCol) return;
  fromCol.projects = fromCol.projects.filter(p => p.id !== projectId);
  fromCol.count = fromCol.projects.length;
  proj.status = toStatus;
  toCol.projects.unshift(proj);
  toCol.count = toCol.projects.length;
  renderBoard();
}

function escHtml(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
